import { CurrencyDollarIcon } from "@phosphor-icons/react/dist/csr/CurrencyDollar";
import { QrCodeIcon } from "@phosphor-icons/react/dist/csr/QrCode";
import { useCallback } from "react";
import { AttributionBadge } from "@/core/components/attribution-badge";
import { Button } from "@volca/ui/components/ui/button";
import { SimpleSidebarHeader } from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { formatPhoneNumber } from "@/core/utils/phone";
import {
  ACTIVE_REFERRAL_STATUSES,
  REFERRAL_STATUS,
} from "@/referrals/constants";
import { IReferral } from "@/referrals/referral";
import { ReferralOptionsDropdown } from "./referral-options-dropdown";
import { SidebarBadge } from "./sidebar-badge";

interface SidebarHeaderProps {
  referral: IReferral;
  actionButton: React.ReactNode;
  onClose: () => void;
  onPayout: () => void;
  onLost?: () => void;
  onMoveToPaid?: () => void;
  hideIconPayoutButton?: boolean;
  onDelete?: () => void;
}

export const SidebarHeader = ({
  referral,
  actionButton,
  onClose,
  onPayout,
  onLost,
  onMoveToPaid,
  hideIconPayoutButton = false,
  onDelete,
}: SidebarHeaderProps) => {
  const signupSource = referral.advocate.signup_source;

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <div className="w-full shrink-0">
      <SimpleSidebarHeader
        className="px-6 pb-3"
        title={referral.potential_customer.name || "Unnamed referral"}
        description={
          formatPhoneNumber(referral.potential_customer.phone_number) ||
          "No phone number"
        }
        onClose={handleClose}
      />
      <Stack align="start" gap={4} className="w-full px-6 pb-4">
        <Stack direction="row" gap={2} align="center" className="flex-wrap">
          <SidebarBadge status={referral.status} />
          {signupSource && (
            <AttributionBadge
              icon={<QrCodeIcon className="h-4 w-4" />}
              signupSource={signupSource}
              section="referrals"
            />
          )}
        </Stack>
        <Stack className="w-full" align="start" gap={4} direction="row">
          {actionButton}
          {!hideIconPayoutButton &&
            referral.status !== REFERRAL_STATUS.CLOSED &&
            ACTIVE_REFERRAL_STATUSES.includes(referral.status) && (
              <Button
                variant="ghost"
                size="icon"
                type="button"
                aria-label="Pay out"
                onClick={onPayout}
              >
                <CurrencyDollarIcon />
              </Button>
            )}
          <ReferralOptionsDropdown
            referral={referral}
            onLost={onLost}
            onMoveToPaid={onMoveToPaid}
            onDelete={onDelete}
          />
        </Stack>
      </Stack>
    </div>
  );
};
